import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { BADGE_SIZES } from './BadgeConstants';

const resolveAsset = (url) =>
  url && url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

export const BadgePreview = React.forwardRef(({ design, event, sampleData }, ref) => {
  const size = BADGE_SIZES[design.size];
  const logoUrl = resolveAsset(design.logoUrl || event?.branding?.logo_url);

  return (
    <div
      ref={ref}
      className="mx-auto shadow-lg"
      data-testid="badge-preview"
      style={{
        width: `${size.width * 3}px`,
        height: `${size.height * 3}px`,
        backgroundColor: design.backgroundColor,
        borderRadius: `${design.borderRadius}px`,
        border: '1px solid #e5e7eb',
        fontFamily: design.fontFamily,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Background Image */}
      {design.backgroundImageUrl && (
        <div
          style={{
            position: 'absolute', inset: 0,
            backgroundImage: `url(${design.backgroundImageUrl})`,
            backgroundSize: design.backgroundFit,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: (design.backgroundOpacity || 100) / 100,
            zIndex: 0,
          }}
        />
      )}

      {/* Header */}
      {design.headerStyle !== 'none' && (
        <div
          style={{
            padding: '8px 12px', position: 'relative', zIndex: 1,
            background: design.headerStyle === 'gradient'
              ? `linear-gradient(135deg, ${design.primaryColor}, ${design.secondaryColor})`
              : design.primaryColor,
            textAlign: design.layout === 'centered' ? 'center' : design.layout,
          }}
        >
          {design.showLogo && logoUrl && (
            <img src={logoUrl} alt="Event Logo" data-testid="badge-preview-logo"
                 onError={(e) => { e.currentTarget.style.display = 'none'; }}
                 style={{ maxHeight: '24px', maxWidth: '80%', marginBottom: '4px' }} />
          )}
          {design.showEventName && (
            <div style={{ color: 'white', fontSize: '10px', fontWeight: '500' }}>{event?.name || 'Event Name'}</div>
          )}
        </div>
      )}

      {/* Tier Badge (Top) */}
      {design.showTier && design.tierPosition === 'top' && sampleData.tier && (
        <div style={{ textAlign: 'center', padding: '6px', position: 'relative', zIndex: 1 }}>
          <span style={{ display: 'inline-block', padding: '2px 12px', backgroundColor: design.primaryColor, color: 'white', borderRadius: '12px', fontSize: '10px', fontWeight: '600' }}>
            {sampleData.tier}
          </span>
        </div>
      )}

      {/* Main Content */}
      <div
        style={{
          flex: 1, padding: '12px', position: 'relative', zIndex: 1,
          textAlign: design.layout === 'centered' ? 'center' : design.layout,
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}
      >
        <div style={{ fontSize: `${design.nameSize}px`, fontWeight: 'bold', color: design.textColor, marginBottom: '4px' }}>
          {sampleData.name || `${sampleData.first_name} ${sampleData.last_name}`}
        </div>
        {design.showCompany && sampleData.company && (
          <div style={{ fontSize: `${design.companySize}px`, color: design.textColor, opacity: 0.8 }}>{sampleData.company}</div>
        )}
        {design.showTitle && sampleData.title && (
          <div style={{ fontSize: `${design.titleSize}px`, color: design.textColor, opacity: 0.6 }}>{sampleData.title}</div>
        )}
      </div>

      {/* QR Code */}
      {design.showQrCode && (
        <div style={{ textAlign: 'center', padding: '8px', position: 'relative', zIndex: 1 }}>
          <QRCodeSVG value={sampleData.id || 'sample'} size={design.qrSize} level="M" fgColor={design.textColor} bgColor="transparent" />
        </div>
      )}

      {/* Tier Badge (Bottom) */}
      {design.showTier && design.tierPosition === 'bottom' && sampleData.tier && (
        <div style={{ textAlign: 'center', padding: '6px', position: 'relative', zIndex: 1 }}>
          <span style={{ display: 'inline-block', padding: '2px 12px', backgroundColor: design.primaryColor, color: 'white', borderRadius: '12px', fontSize: '10px', fontWeight: '600' }}>
            {sampleData.tier}
          </span>
        </div>
      )}

      {/* Badge ID */}
      {design.showBadgeId && (
        <div style={{ textAlign: 'center', padding: '4px', fontSize: '8px', color: design.textColor, opacity: 0.5, letterSpacing: '1px', position: 'relative', zIndex: 1 }}>
          DEL-{(sampleData.id || 'SAMPLE').slice(-6).toUpperCase()}
        </div>
      )}
    </div>
  );
});

BadgePreview.displayName = 'BadgePreview';
